import { useEffect } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useUserProfile } from "@pontimed/auth-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ExternalLink, MessageSquare, Share2, Star } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { PageHeader } from "@/components/layout/page-header";
import { buttonVariants } from "@/components/ui/button-variants";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { StatCard } from "@/components/ui/stat-card";
import { buildWebUrl } from "@/lib/web-url";

export const Route = createFileRoute("/_authenticated/account/provider/reviews")({
  component: ProviderReviewsPage,
});

interface SidebarData {
  provider?: { ratingAvg: number | null; reviewCount: number } | null;
}

interface ProviderBasicResponse {
  provider: { id: string; primaryCountry: string | null };
}

function ProviderReviewsPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const profileQuery = useUserProfile();
  const hasProvider = profileQuery.data?.hasProviderProfile === true;

  // Soft client-side gate: legacy redirects when the session has no providerId.
  // The apps/app session JWT does not carry providerId, so we read
  // hasProviderProfile from /api/account/user/profile instead. The API also
  // enforces the gate server-side on every provider endpoint.
  useEffect(() => {
    if (profileQuery.data && profileQuery.data.hasProviderProfile === false) {
      navigate({
        to: "/account",
        search: { reason: "no-provider-profile" } as never,
        replace: true,
      });
    }
  }, [profileQuery.data, navigate]);

  // Real numbers from sidebar-data (same source as the menu badge). There is
  // no provider review-list endpoint yet, so the reviews themselves are read
  // on the public profile; "Response rate" stays hidden until it exists.
  const sidebarQ = useQuery({
    queryKey: ["api", "/api/account/sidebar-data"],
    queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
  });
  const providerQ = useQuery({
    queryKey: ["api", "/api/account/provider", "basic"],
    queryFn: () => apiFetch.get<ProviderBasicResponse>("/api/account/provider"),
    enabled: hasProvider,
  });

  const stats = sidebarQ.data?.provider ?? null;
  const reviewCount = stats?.reviewCount ?? 0;
  const provider = providerQ.data?.provider;
  const publicProfileUrl = provider
    ? buildWebUrl(
        `provider/${provider.id}`,
        i18n.locale,
        (provider.primaryCountry || "ro").toLowerCase(),
      )
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={<Trans>Reviews</Trans>}
        description={<Trans>Patient ratings and your responses.</Trans>}
      />

      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label={<Trans>Average rating</Trans>}
          value={stats?.ratingAvg != null ? stats.ratingAvg : "—"}
          format={{ minimumFractionDigits: 1, maximumFractionDigits: 1 }}
          icon={Star}
          tone="accent"
          loading={sidebarQ.isPending}
        />
        <StatCard
          label={<Trans>Total reviews</Trans>}
          value={reviewCount}
          icon={MessageSquare}
          tone="primary"
          loading={sidebarQ.isPending}
        />
      </div>

      {sidebarQ.isError ? (
        <LoadErrorState
          title={<Trans>Could not load your reviews.</Trans>}
          onRetry={() => void sidebarQ.refetch()}
          retrying={sidebarQ.isFetching}
        />
      ) : !sidebarQ.isPending && reviewCount > 0 ? (
        <div className="border-border bg-card flex flex-col items-start gap-3 rounded-xl border p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-muted-foreground text-sm">
            <Trans>Your reviews are shown on your public profile, where patients read them.</Trans>
          </p>
          {publicProfileUrl ? (
            <a
              href={publicProfileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants({ variant: "outline", size: "touch" }), "shrink-0")}
            >
              <ExternalLink className="size-4" aria-hidden />
              <Trans>See reviews on your public profile</Trans>
            </a>
          ) : null}
        </div>
      ) : !sidebarQ.isPending ? (
        <div className="border-border bg-card rounded-xl border">
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
            <div className="bg-muted rounded-full p-4">
              <Star className="text-muted-foreground size-8" aria-hidden />
            </div>
            <p className="text-foreground mt-4 text-sm font-medium">
              <Trans>No reviews yet</Trans>
            </p>
            <p className="text-muted-foreground mt-1 max-w-sm text-xs">
              <Trans>
                Reviews from patients you've consulted will appear here once they leave feedback.
              </Trans>
            </p>
            <Link
              to="/account/provider/booking-widget"
              className={cn(buttonVariants({ size: "touch" }), "mt-4")}
            >
              <Share2 className="size-4" aria-hidden />
              <Trans>Share your booking link</Trans>
            </Link>
          </div>
        </div>
      ) : null}

      <AccountSecurityBanner />
    </div>
  );
}
